import { useState, type ComponentProps } from 'react'
import { toast } from 'sonner'
import type { ActionPopover } from '@/components/action-popover'
import { InlineFieldInput } from '@/components/inline-field-group'
import { Label } from '@/components/ui/label'
import { formatApiErrorMessage } from '@/lib/api-error'
import { formatMoney } from '@/lib/utils'
import type { components } from '@/api/schema'
import type { PopoverRecoveryMode } from './action-recovery-modes'
import {
  useAcceptFreightChargeAction,
  useRecordCancellationAction,
} from '@/features/purchase-orders/hooks'

type PurchaseOrderAction = NonNullable<
  components['schemas']['PurchaseOrderDetailRead']['actions']
>[number]

export function recoveryHandlers(success: string, failure: string, onClose: () => void) {
  return {
    onSuccess: () => {
      toast.success(success)
      onClose()
    },
    onError: (error: unknown) => toast.error(formatApiErrorMessage(failure, error)),
  }
}

export type ActionPopoverConfig = Omit<
  ComponentProps<typeof ActionPopover>,
  'trigger' | 'anchorOnly' | 'open' | 'onOpenChange'
>

/** Freight and cancellation as the card's popover content (USA-1536); `mode` is the one shown. */
export function useRecoveryPopover({
  mode,
  open,
  action,
  poId,
  onClose,
}: {
  mode: PopoverRecoveryMode | null
  open: boolean
  action: PurchaseOrderAction
  poId: string
  onClose: () => void
}): ActionPopoverConfig | null {
  const quotedFreight = action.charge_amount?.toString() ?? ''
  const [freightAmount, setFreightAmount] = useState(quotedFreight)
  const [wasOpen, setWasOpen] = useState(open)
  // Every opening starts again from the quoted charge.
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setFreightAmount(quotedFreight)
  }
  const freight = useAcceptFreightChargeAction(poId)
  const cancel = useRecordCancellationAction(poId)
  const pending = freight.isPending || cancel.isPending

  if (mode === 'freight') {
    const amount = Number(freightAmount)
    const fieldId = `recovery-freight-${action.id}`
    return {
      title: 'Add freight',
      children: (
        <div className="space-y-1">
          <Label htmlFor={fieldId} className="text-xs text-muted-foreground">
            Freight charge ($)
          </Label>
          <InlineFieldInput
            id={fieldId}
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={freightAmount}
            onChange={(event) => setFreightAmount(event.target.value)}
          />
        </div>
      ),
      confirmLabel: `Manufacturer ships · add ${amount > 0 ? formatMoney(amount) : 'freight'}`,
      pending: freight.isPending,
      pendingLabel: 'Updating PO...',
      canConfirm: Boolean(freightAmount) && amount > 0 && !pending,
      onConfirm: () =>
        freight.mutate(
          { actionId: action.id, freightAmount: amount },
          recoveryHandlers('Manufacturer ships; freight added to PO', 'Freight not updated', onClose),
        ),
    }
  }

  if (mode === 'cancel') {
    return {
      title: 'Record cancellation',
      confirmLabel: 'Cancel PO & close its action items',
      confirmVariant: 'destructive',
      pending: cancel.isPending,
      pendingLabel: 'Cancelling...',
      canConfirm: !pending,
      onConfirm: () =>
        cancel.mutate(
          { actionId: action.id },
          recoveryHandlers('PO cancelled', 'Cancellation not recorded', onClose),
        ),
    }
  }

  return null
}
